import { NO_RUN, RunScope, runOn, UNKNOWN_RUN } from "@/components/CreditConfirmModal/runScope";
import { AiApplyType } from "@/enum/list.enum";

/** What a custom-column form knows about the rows its Apply-to choice reaches. */
export interface CustomColumnRunInput {
	applyTo: AiApplyType;
	/** The table's loaded rows: "First 10" submits the first ten of these. */
	loadedRowCount: number;
	/** The leads the form submits for "Currently selected". */
	selectedCount: number;
	/** The table's total for its current view (search, tab, filters). */
	viewCount: number;
}

/** The first ten loaded rows, the ids "First 10" submits. */
const firstTen = (loadedRowCount: number): number => Math.min(loadedRowCount, 10);

/**
 * Submitted ids, or every new lead of the list: the AI column's run reads an empty id list as the
 * whole list (`getPeopleToApplyPromptsTo`), which the form cannot count.
 */
const submittedOrWholeList = (count: number): RunScope => (count > 0 ? runOn(count) : UNKNOWN_RUN);

/**
 * The leads an AI column's run covers (PREC-4263 review S1), as `list-update-columns.service`
 * resolves them: First 10 and Currently selected run on the ids the form submits; Currently
 * filtered on the table's view, re-read from the filters it sends (`currentlyFilteredIds`), so the
 * table's total, and nothing without filters; All and All including assigned on every lead of the
 * list in those statuses, blacklisted and still-searching leads included, which no count the
 * list's tabs show matches, so the run decides it.
 */
export function aiColumnRunScope(input: CustomColumnRunInput & { hasFilters: boolean }): RunScope {
	switch (input.applyTo) {
		case AiApplyType.NONE:
			return NO_RUN;
		case AiApplyType.FIRST_10:
			return submittedOrWholeList(firstTen(input.loadedRowCount));
		case AiApplyType.CURRENTLY_SELECTED:
			return submittedOrWholeList(input.selectedCount);
		case AiApplyType.CURRENTLY_FILTERED:
			return input.hasFilters ? runOn(input.viewCount) : UNKNOWN_RUN;
		default:
			return UNKNOWN_RUN;
	}
}

/**
 * The leads a Deep Research run covers (PREC-4263 review S1), as `scheduleBulkDeepResearchByList`
 * resolves them: First 10 and Currently selected run on the ids the form submits (none when it
 * submits none); every other choice sends a select-all of the table's view with its filters, so
 * Currently filtered, All and All including assigned all cover the table's total. A bulk run
 * from the table's selection covers the ids it sends, or the view for a select-all.
 */
export function deepResearchRunScope(
	input: CustomColumnRunInput & { isOnlySelected: boolean; isAllSelected: boolean }
): RunScope {
	if (input.isOnlySelected) {
		return runOn(input.isAllSelected ? input.viewCount : input.selectedCount);
	}

	switch (input.applyTo) {
		case AiApplyType.NONE:
			return NO_RUN;
		case AiApplyType.FIRST_10:
			return runOn(firstTen(input.loadedRowCount));
		case AiApplyType.CURRENTLY_SELECTED:
			return runOn(input.selectedCount);
		default:
			return runOn(input.viewCount);
	}
}

/** The count a run's button names: the leads it covers, 0 when none or unknown. */
export const runScopeCount = (scope: RunScope): number =>
	scope.kind === "count" ? scope.count : 0;
